import { Injectable } from '@angular/core';

interface ExtensionMessage {
  checkStatus?: boolean;
  addBookmarkUrl?: string;
  deleteBookmarkUrl?: string;
  title?: string;
  bookCoverUrl?: string;
  expired?: number;
}

interface ChromeRuntime {
  lastError?: unknown;
  sendMessage: (
    extensionId: string,
    message: ExtensionMessage,
    callback: (response?: { success?: boolean }) => void,
  ) => void;
}

interface ExtensionReply {
  origin?: string;
  response?: boolean;
}

export interface OfflineBookmark {
  url: string;
  title: string;
  coverUrl: string;
  /** Loan end (ms since epoch), shown by the extension. */
  expired: number;
}

type Browser = 'chrome' | 'firefox' | 'other';

/**
 * Bridge with the "Nubereader offline" browser extension, which keeps the downloaded books as
 * bookmarks and reopens them without connection. Same contract as NR1.
 */
@Injectable({
  providedIn: 'root',
})
export class NubereaderExtensionService {
  private static readonly CEIBAL_OTK_ID = 'E0435';
  private static readonly CHROME_ID = 'ndnnpcppkhpniejplildmghepphgplcp';
  private static readonly CHROME_ID_CEIBAL = 'jajnhbonobojdghnpiedknciophllngc';
  private static readonly CHROME_STORE_URL =
    'https://chrome.google.com/webstore/detail/nubereader-offline/ndnnpcppkhpniejplildmghepphgplcp';
  private static readonly CHROME_STORE_URL_CEIBAL =
    'https://chrome.google.com/webstore/detail/lector-biblioteca-ceibal/jajnhbonobojdghnpiedknciophllngc';
  private static readonly FIREFOX_STORE_URL =
    'https://addons.mozilla.org/firefox/addon/nubereader-offline/';
  private static readonly FIREFOX_STORE_URL_CEIBAL =
    'https://addons.mozilla.org/firefox/addon/lector-bibliotecas-ceibal/';
  private static readonly PAGE_ORIGIN = 'odilo-nubereader';
  private static readonly EXTENSION_ORIGIN = 'extension-nubereader';
  /** The Firefox content script does not answer when the extension is missing. */
  private static readonly REPLY_TIMEOUT_MS = 1000;

  private readonly browser = NubereaderExtensionService.detectBrowser(navigator.userAgent);

  isSupportedBrowser(): boolean {
    return this.browser !== 'other';
  }

  storeUrl(otkId: string): string | undefined {
    const isCeibal = otkId === NubereaderExtensionService.CEIBAL_OTK_ID;
    if (this.browser === 'chrome') {
      return isCeibal
        ? NubereaderExtensionService.CHROME_STORE_URL_CEIBAL
        : NubereaderExtensionService.CHROME_STORE_URL;
    }
    if (this.browser === 'firefox') {
      return isCeibal
        ? NubereaderExtensionService.FIREFOX_STORE_URL_CEIBAL
        : NubereaderExtensionService.FIREFOX_STORE_URL;
    }
    return undefined;
  }

  async isInstalled(otkId: string): Promise<boolean> {
    return this.send(otkId, { checkStatus: true });
  }

  async addBook(otkId: string, bookmark: OfflineBookmark): Promise<boolean> {
    return this.send(otkId, {
      addBookmarkUrl: bookmark.url,
      title: bookmark.title,
      bookCoverUrl: bookmark.coverUrl,
      expired: bookmark.expired,
    });
  }

  /** Removes every bookmark whose URL contains `urlFragment` (the extension matches by substring). */
  async removeBook(otkId: string, urlFragment: string): Promise<boolean> {
    return this.send(otkId, { deleteBookmarkUrl: urlFragment });
  }

  private async send(otkId: string, message: ExtensionMessage): Promise<boolean> {
    if (this.browser === 'chrome') return NubereaderExtensionService.sendToChrome(otkId, message);
    if (this.browser === 'firefox') return NubereaderExtensionService.sendToFirefox(message);
    return false;
  }

  private static async sendToChrome(otkId: string, message: ExtensionMessage): Promise<boolean> {
    const runtime = NubereaderExtensionService.chromeRuntime();
    if (!runtime) return false;
    const extensionId =
      otkId === NubereaderExtensionService.CEIBAL_OTK_ID
        ? NubereaderExtensionService.CHROME_ID_CEIBAL
        : NubereaderExtensionService.CHROME_ID;
    return new Promise((resolve) => {
      try {
        runtime.sendMessage(extensionId, message, (response) => {
          resolve(!runtime.lastError && Boolean(response?.success));
        });
      } catch {
        resolve(false);
      }
    });
  }

  /** `chrome.runtime` only exists in the page when an extension allows it to connect. */
  private static chromeRuntime(): ChromeRuntime | undefined {
    const chromeGlobal: unknown = Reflect.get(globalThis, 'chrome');
    if (typeof chromeGlobal !== 'object' || chromeGlobal === null) return undefined;
    const runtime: unknown = Reflect.get(chromeGlobal, 'runtime');
    return NubereaderExtensionService.isChromeRuntime(runtime) ? runtime : undefined;
  }

  private static isChromeRuntime(value: unknown): value is ChromeRuntime {
    return (
      typeof value === 'object' &&
      value !== null &&
      typeof Reflect.get(value, 'sendMessage') === 'function'
    );
  }

  private static async sendToFirefox(message: ExtensionMessage): Promise<boolean> {
    const listening = new AbortController();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        listening.abort();
        resolve(false);
      }, NubereaderExtensionService.REPLY_TIMEOUT_MS);
      window.addEventListener(
        'message',
        (event: MessageEvent<ExtensionReply | undefined>) => {
          if (
            event.source !== window ||
            event.data?.origin !== NubereaderExtensionService.EXTENSION_ORIGIN
          ) {
            return;
          }
          clearTimeout(timer);
          listening.abort();
          resolve(Boolean(event.data.response));
        },
        { signal: listening.signal },
      );
      window.postMessage(
        { ...message, origin: NubereaderExtensionService.PAGE_ORIGIN },
        window.location.origin,
      );
    });
  }

  private static detectBrowser(userAgent: string): Browser {
    if (/firefox\//iu.test(userAgent)) return 'firefox';
    if (/chrome\/|chromium\/|crios\//iu.test(userAgent)) return 'chrome';
    return 'other';
  }
}
